// apps/cli/src/commands/bootstrap/profile.ts
import * as readline from "node:readline/promises";
import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { colors } from "../../lib/output.js";
import { getSignedInUserIdentity } from "../../lib/founder-identity.js";
import { collectProfile, ADVISOR_SKIPPED_LINE } from "../../lib/profile-collect.js";
import {
  writeOnboardingProfile, readOnboardingProfile, toOnboardingBlock, type StoredOnboardingProfile,
} from "../../lib/onboarding-profile-store.js";
import { resolveSeedContext, applyProfileToBrains } from "../../lib/company-profile-seed.js";
import { openChatInvite } from "../../lib/chat-invite.js";

/**
 * Confirm the two facts the deployed advisors need, and hand the founder something
 * to talk to while the cloud installer runs.
 *
 * This is runbook step 4b. It replaces `m8t bootstrap ui`, which used to deploy a
 * dedicated intake agent and serve a local chat app to interview the founder — an
 * interview that had already collapsed to one confirmation and one question, behind
 * a whole product vertical's worth of machinery.
 *
 * What it actually repairs: `memory/founder.md` is the file the deployed Ezra reads
 * to learn how to reach the founder and who their Microsoft startup advisor is. Its
 * only source used to be a Foundry conversation tagged `agent="ezra-intake"`, and
 * since that agent stopped being deployed there has been nothing to read — so no
 * install has written that file at all. `founder_email` is CC + Reply-To on every
 * advisor email AND a required field for the send, so without it the outbound
 * advisor path is not degraded, it is dead.
 *
 * Safe to re-run at any point, before or after the install finishes: it rewrites the
 * local answers and re-attempts the seed, and the seed is a no-op when nothing changed.
 */
export class BootstrapProfileCommand extends M8tCommand {
  static paths = [["bootstrap", "profile"]];
  static usage = Command.Usage({
    description: "Confirm your email + your Microsoft startup advisor, and open Ezra to talk to while the install runs.",
    details: [
      "Run after `m8t bootstrap launch`, in parallel with `status --watch`. Records the two",
      "facts your deployed advisors need — the address Ezra copies you on, and the Microsoft",
      "startup advisor it can escalate to — and points you at the hosted Ezra for the wait.",
      "",
      "Non-interactive by default: pass the answers as flags and nothing is prompted. With a",
      "terminal, any answer you did not pass is asked for, with your Azure sign-in offered as",
      "the default. Idempotent — re-run it any time to correct or fill in an answer.",
      "",
      "Nothing here blocks the install, and chat never fails it.",
    ].join("\n"),
    examples: [
      ["Record both answers", "$0 bootstrap profile --founder-email you@example.com --advisor-name 'Sam Lee' --advisor-email sam@example.com"],
      ["Record the email, skip the advisor", "$0 bootstrap profile --founder-email you@example.com --no-advisor"],
      ["Answer the questions at a terminal", "$0 bootstrap profile"],
      ["Skip the browser hand-off", "$0 bootstrap profile --founder-email you@example.com --print"],
    ],
  });

  founderEmail = Option.String("--founder-email", { description: "The founder's contact address — Ezra copies them on its outbound mail." });
  advisorName = Option.String("--advisor-name", { description: "Their Microsoft startup advisor's name." });
  advisorEmail = Option.String("--advisor-email", { description: "Their Microsoft startup advisor's email." });
  noAdvisor = Option.Boolean("--no-advisor", false, { description: "Record that they have no startup advisor to add (or don't know it yet)." });
  noChat = Option.Boolean("--no-chat", false, { description: "Record the answers without opening the hosted Ezra." });
  print = Option.Boolean("--print", false, { description: "Print the chat link instead of opening a browser (headless / SSH)." });

  async executeCommand(): Promise<number> {
    const stdin = this.context.stdin as NodeJS.ReadStream;
    const stdout = this.context.stdout as NodeJS.WriteStream;
    // stdin, not stdout: we are deciding whether anyone can ANSWER, and the runbook's
    // coding agent runs us with a pipe on both. Keying off stdout would hang it.
    const isTty = stdin.isTTY === true;

    const flags = {
      founderEmail: typeof this.founderEmail === "string" ? this.founderEmail : undefined,
      advisorName: typeof this.advisorName === "string" ? this.advisorName : undefined,
      advisorEmail: typeof this.advisorEmail === "string" ? this.advisorEmail : undefined,
      noAdvisor: this.noAdvisor === true,
    };
    const [identity, previous] = await Promise.all([
      getSignedInUserIdentity(),
      readOnboardingProfile(),
    ]);

    // Only open a terminal interface when something might actually be asked. A run
    // that supplied every answer as a flag has nothing to prompt for.
    const mayAsk = isTty && (
      !(flags.founderEmail ?? "").trim() ||
      !(flags.noAdvisor || (flags.advisorName ?? "").trim() || (flags.advisorEmail ?? "").trim())
    );
    const rl = mayAsk ? readline.createInterface({ input: stdin, output: stdout }) : null;
    let collected;
    try {
      collected = await collectProfile({
        flags,
        identity,
        previous,
        isTty,
        ask: async (question) => rl ? await rl.question(question) : "",
        now: () => new Date().toISOString(),
      });
    } finally {
      rl?.close();
    }
    if (!collected.ok) throw collected.error;
    const profile = collected.profile;

    await writeOnboardingProfile(profile);
    this.context.stdout.write(`${colors.success("✓")} Recorded — Ezra will copy you at ${colors.field(profile.founderEmail)}.\n`);
    if (profile.advisor === null) this.context.stdout.write(colors.dim(ADVISOR_SKIPPED_LINE));

    await this.seedIfBrainsExist(profile);

    if (this.noChat !== true) {
      this.context.stdout.write(await openChatInvite({ print: this.print === true }));
    }
    return 0;
  }

  /**
   * Seed now if the brains already exist, otherwise say who will.
   *
   * Best-effort on purpose. This command runs mid-install by design, when there is
   * usually nothing to seed yet — `bootstrap status --watch` does it at `done`. But
   * re-running this after the install should not leave a founder wondering whether
   * their correction landed, so when the brains ARE there we seed immediately.
   *
   * A failure here is reported and swallowed: the answers are already safely on disk,
   * and the install path will retry the seed on its own.
   */
  private async seedIfBrainsExist(profile: StoredOnboardingProfile): Promise<void> {
    let ctx: Awaited<ReturnType<typeof resolveSeedContext>> = null;
    try {
      ctx = await resolveSeedContext({});
    } catch {
      // No bootstrap state yet (run before `launch`, or from another machine).
      ctx = null;
    }
    if (!ctx) {
      this.context.stdout.write(
        `  ${colors.dim("Your advisors will pick this up when the install finishes.")}\n`,
      );
      return;
    }
    try {
      await applyProfileToBrains({
        block: toOnboardingBlock(profile),
        brainRepos: ctx.brainRepos,
        branch: "main",
        appCreds: ctx.appCreds,
        subscriptionId: ctx.subscriptionId,
        azIdentity: { name: profile.founderName, email: profile.founderEmail },
        contactsOnly: true,
        advisorCleared: profile.advisorCleared === true,
      });
      this.context.stdout.write(
        `${colors.success("✓")} Your advisors now know how to reach you (seeded ${ctx.brainRepos.join(", ")}).\n`,
      );
    } catch (e) {
      this.context.stderr.write(
        `  ${colors.dim(`saved, but the brain seed didn't land yet: ${e instanceof Error ? e.message : String(e)}`)}\n` +
        `  ${colors.hint("it retries when the install finishes, or run:")} m8t bootstrap seed-profile\n`,
      );
    }
  }
}
